import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FRONT_URL, deleteUserFromUserEvalList, sendLaunchEmails, sendReminderEmails, updateTimRol, updateUser } from "../../axios/axiosFunctions";
import ViewResponsesDialog from "../ViewResponsesDialog";

const defaultUser = {
    firstName: "",
    lastName:"",
    email: "",
    type: "",
}

const UsersActions = ({row,api}) => {

    const {slugAssessment} = useParams();
    const [openDialogEdit, setOpenDialogEdit] = useState(false);
    const [openDialogLink, setOpenDialogLink] = useState(false);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [openDialogEmail, setOpenDialogEmail] = useState(false);
    const [emailType, setEmailType] = useState("launch");
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailResult, setEmailResult] = useState(null);
    const [alertSeverity, setAlertSeverity] = useState("error");
    const [alertMsg, setAlertMsg] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [openViewResponses, setOpenViewResponses] = useState(false);
    const [userForm, setUserForm] = useState(defaultUser);
    const { firstName, lastName, email, type } = userForm;

    useEffect(() => {
        initUser();
    }, [])

    const initUser = ()=>{
        setUserForm({
            firstName: row.firstName || "",
            lastName: row.lastName || "",
            email: row.email || "",
            type: ["manager","direct","peer","otherColleague"].includes(row.type) ? row.type : "",
        });
    }

    const handleCloseDialogEdit = ()=>{
        setOpenDialogEdit(false);
    }

    const handleCloseDialogLink = ()=>{
        setOpenDialogLink(false);
    }

    const handleCloseDialogDelete = ()=>{
        setOpenDialogDelete(false);
    }

    
    const handleEdit = ()=>{
        initUser();
        setOpenDialogEdit(true);
    }
    
    const handleLink = ()=>{
        setOpenDialogLink(true);
    }

    const handleOpenDelete = ()=>{
        setOpenDialogDelete(true);
    }

    const handleChange = (e)=>{
        const {name,value} = e.target;
        setUserForm({...userForm,[name]:value});
    }

    const handleCancelEdit = ()=>{
        initUser();
        handleCloseDialogEdit();
    }
    
    const submitUser = async()=>{
        if(!firstName || !lastName || !email || !type || !["manager","direct","peer","otherColleague"].includes(type)){
            setAlertMsg("All fields are required. Please select a valid Type (Manager, Direct Report, Peer, or Other Colleague).");
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 3000);
            return;
        }

        const dataUser = {
            firstName,
            lastName,
            email
        }

        const dataRol = {
            rol:type
        }

        const resUser = await updateUser(row.userId,dataUser);
        
        if(!resUser.user){
            setAlertMsg(resUser.msg);
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }

        await updateTimRol(row.userId,row.groupId,dataRol,row.owner);
        api.updateRows([{id:row.id,firstName,lastName,email,type}]);
        handleCloseDialogEdit();
    }

    const deleteUser = async()=>{        
        const res = await deleteUserFromUserEvalList(row.groupId,row.userId,row.owner);
        if(!res){
            setAlertMsg("Error while deleting, please try later or contact an admin");
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }
        row.callUsersEval(row.owner,row.groupId);
        row.callUsersListToAdd(row.owner,row.groupId);
        handleCloseDialogDelete();
    }

    const handleOpenEmail = (type) => {
        setEmailType(type);
        setEmailResult(null);
        setOpenDialogEmail(true);
    };

    const handleSendEmailToReviewer = async () => {
        setSendingEmail(true);
        setEmailResult(null);
        try {
            const data = {
                slug: slugAssessment,
                frontUrl: FRONT_URL,
                revieweeIds: [row.owner],
                reviewerIds: [row.userId],
            };
            let res;
            if (emailType === "launch") {
                res = await sendLaunchEmails(row.groupId, data);
                setEmailResult({ severity: "success", msg: `Launch email sent to ${row.email}.` });
            } else {
                res = await sendReminderEmails(row.groupId, data);
                if (res.skippedCompleted > 0) {
                    setEmailResult({ severity: "info", msg: `Reviewer already completed — no reminder sent.` });
                } else {
                    setEmailResult({ severity: "success", msg: `Reminder sent to ${row.email}.` });
                }
            }
        } catch (err) {
            setEmailResult({ severity: "error", msg: "Failed to send email. Please try again." });
        }
        setSendingEmail(false);
    };

    return (
    <Box sx={{width: "100%",height: "100%",display: "flex",justifyContent: "space-evenly", alignItems: "center"}}>
        <Tooltip title="View Responses">
            <IconButton onClick={() => setOpenViewResponses(true)} size="small">
                <VisibilityIcon sx={{color:"#4caf50",fontSize:"20px"}}/>
            </IconButton>
        </Tooltip>
        <Tooltip title="Send launch email">
            <IconButton onClick={() => handleOpenEmail("launch")} size="small">
                <EmailIcon sx={{color:"#1976d2",fontSize:"20px"}}/>
            </IconButton>
        </Tooltip>
        <Tooltip title="Send reminder">
            <IconButton onClick={() => handleOpenEmail("reminder")} size="small">
                <NotificationsActiveIcon sx={{color:"#0288d1",fontSize:"20px"}}/>
            </IconButton>
        </Tooltip>
        <IconButton onClick={handleEdit}>
            <EditIcon sx={{color:"orange"}}/>
        </IconButton>
        <IconButton onClick={handleLink}>
            <InsertLinkIcon sx={{color:"orange"}}/>
        </IconButton>
        <IconButton onClick={handleOpenDelete}>
            <DeleteIcon sx={{color:"red"}}/>
        </IconButton>
        <Dialog open={openDialogEmail} onClose={() => { if (!sendingEmail) { setOpenDialogEmail(false); setEmailResult(null); } }}>
            <DialogTitle sx={{ fontWeight: "bold" }}>
                {emailType === "launch" ? "Send Launch Email" : "Send Reminder"}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 1 }}>
                    {emailType === "launch"
                        ? <>Send launch email to reviewer <strong>{row.firstName} {row.lastName}</strong> ({row.email}) with their direct feedback link.</>
                        : <>Send reminder to reviewer <strong>{row.firstName} {row.lastName}</strong> ({row.email}) to complete their feedback.</>
                    }
                </Typography>
                {emailResult && (
                    <Alert severity={emailResult.severity} sx={{ mt: 1 }}>{emailResult.msg}</Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px" }}>
                <Button onClick={() => { setOpenDialogEmail(false); setEmailResult(null); }} disabled={sendingEmail} color="inherit">Cancel</Button>
                {!emailResult ? (
                    <Button onClick={handleSendEmailToReviewer} variant="contained" color={emailType === "launch" ? "primary" : "info"} disabled={sendingEmail}
                        startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : (emailType === "launch" ? <EmailIcon /> : <NotificationsActiveIcon />)}>
                        {sendingEmail ? "Sending..." : "Send"}
                    </Button>
                ) : (
                    <Button onClick={() => { setOpenDialogEmail(false); setEmailResult(null); }} variant="contained">Done</Button>
                )}
            </DialogActions>
        </Dialog>
        <Dialog open={openDialogEdit} onClose={handleCloseDialogEdit}>
            <DialogContent>
                <Typography variant="h5" color="primary">EDIT.</Typography>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Typography variant="subtitle1">First Name</Typography>
                        <TextField onChange={handleChange} value={firstName} name="firstName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Last Name</Typography>
                        <TextField onChange={handleChange} value={lastName} name="lastName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Email</Typography>
                        <TextField onChange={handleChange} value={email} name="email" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Type</Typography>
                        <FormControl sx={{width:"100%"}}>
                            <Select onChange={handleChange} value={type} name="type" fullWidth>
                            <MenuItem value={"manager"}>Manager</MenuItem>
                            <MenuItem value={"direct"}>Direct Report</MenuItem>
                            <MenuItem value={"peer"}>Peer</MenuItem>
                            <MenuItem value={"otherColleague"}>Other Colleague</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {
                        alertOpen &&
                        <Grid size={12}>
                            <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                                {alertMsg}
                            </Alert>
                        </Grid>
                    }
                    <Grid size={12} sx={{display:"flex",justifyContent:"center"}}>
                        <Button variant="contained" onClick={submitUser}>SAVE</Button>
                        <Button variant="contained" onClick={handleCancelEdit} sx={{marginLeft:"20px"}}>CANCEL</Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
        <Dialog open={openDialogLink} onClose={handleCloseDialogLink} maxWidth="md" fullWidth>
            <DialogContent>
                <Typography>LINK:</Typography>
                <TextField fullWidth value={`${FRONT_URL}360/${slugAssessment}/${row.owner}/${row.userId}/${row.groupId}`}/>
            </DialogContent>
        </Dialog>
        <Dialog open={openDialogDelete} onClose={handleCloseDialogDelete}>
            <DialogContent>
                <Typography>Are you sure you want to DELETE this user?</Typography>
                {
                    alertOpen &&
                    <Grid size={12}>
                        <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                            {alertMsg}
                        </Alert>
                    </Grid>
                }
                <Box sx={{display:"flex",justifyContent:"center",marginTop:"20px"}}>
                    <Button onClick={deleteUser} variant="contained" color="error">DELETE</Button>
                    <Button variant="contained" onClick={handleCloseDialogDelete} sx={{marginLeft:"20px"}}>CANCEL</Button>
                </Box>
            </DialogContent>
        </Dialog>
        <ViewResponsesDialog
            open={openViewResponses}
            onClose={() => setOpenViewResponses(false)}
            reviewerId={row.userId}
            reviewerName={`${row.firstName} ${row.lastName}`}
            ownerId={row.owner}
            slug={slugAssessment}
        />
    </Box>
    )
}

export default UsersActions